import React, { useEffect, useRef } from "react";
import styled from "styled-components";
import { atom, useRecoilState, useSetRecoilState } from "recoil";
import PageTitle from "../../components/DefaultStyles/PageTitle";
import {
  PointTabBar,
  PointTabColumn,
  PointTabContainer,
  PointTabText,
} from "../../components/pages/Benefit/Point/PointTab";

import YoutubeMatchingInfo, {
  youtubeInfoFilterState,
} from "./YoutubeMatchingInfo";
import GPTSoloMatchingInfo from "./GPTSoloMatchingInfo";
import { useApi } from "../../hooks/useApi";
import Flex from "../../components/DefaultStyles/Flex";
import { Link } from "react-router-dom";
import GrokSoloMatchingInfo from "./GrokSoloMatchingInfo";
import PerplexitySoloMatchingInfo from "./PerplexitySoloMatchingInfo";

interface IDefItem {
  id: number;
  name: string;
  cnt: string;
  today: string;
}

const youtubeInfoTabState = atom<number>({
  key: "youtubeInfoTabState",
  default: 0,
});

type YoutubeSubTab = 0 | 1 | 2;

const youtubeSubTabState = atom<YoutubeSubTab>({
  key: "youtubeSubTabState",
  default: 0,
});

const YOUTUBE_SUBTAB_SERVSUBSID = [37080, 37000, 37050] as const;

const REFRESH_INTERVAL = 30000;

export default function YoutubeInfo() {
  const [tab, setTab] = useRecoilState(youtubeInfoTabState);
  const [youtubeSubTab, setYoutubeSubTab] = useRecoilState(youtubeSubTabState);
  const setYoutubeFilter = useSetRecoilState(youtubeInfoFilterState);

  const handleYoutubeSubTabClick = (nextSubTab: YoutubeSubTab) => {
    if (nextSubTab !== youtubeSubTab) {
      setYoutubeFilter((prev) => ({ ...prev, page: 0 }));
    }

    setYoutubeSubTab(nextSubTab);
  };

  const {
    response: defResponse,
    loading: defLoading,
    fetchData: fetchDefData,
  } = useApi({
    method: "get",
    url: "version/systemdef",
  });

  const fetchDefDataRef = useRef(fetchDefData);
  fetchDefDataRef.current = fetchDefData;

  useEffect(() => {
    const id = setInterval(() => {
      fetchDefDataRef.current({
        method: "get",
        url: "version/systemdef",
      });
    }, REFRESH_INTERVAL);

    return () => clearInterval(id);
  }, []);

  const defData: IDefItem[] | undefined = defResponse?.data;

  return (
    <>
      <PageTitle title="개인 연간 판매 파티 관리" />

      <Flex gapSize={8} style={{ marginBottom: "16px", alignItems: "center" }}>
        <StatusCardTitle style={{ marginBottom: "0px" }}>
          오늘 현황
        </StatusCardTitle>
        <Link to={"/linkid-manage/status"}>{"설정으로 이동 > "}</Link>
      </Flex>
      {defData && defData.length > 0 && (
        <StatusBoard>
          {defData.map((d) => (
            <StatusCard key={d.id}>
              <StatusCardTitle>{d.name}</StatusCardTitle>
              <StatusRow>
                <StatusItem>
                  <StatusLabel>오늘</StatusLabel>
                  <StatusValue>{d.today}</StatusValue>
                </StatusItem>
                <StatusItem>
                  <StatusLabel>일일 제한</StatusLabel>
                  <StatusValue>{d.cnt}</StatusValue>
                </StatusItem>
              </StatusRow>
            </StatusCard>
          ))}
        </StatusBoard>
      )}

      <RefreshInfo>30초마다 자동 갱신됩니다</RefreshInfo>

      <PointTabContainer>
        <PointTabColumn onClick={() => setTab(0)}>
          <PointTabText isActive={tab === 0}>유튜브 개인</PointTabText>
          <YoutubeTabBar tab={tab} />
        </PointTabColumn>

        {/* <PointTabColumn onClick={() => setTab(1)}>
          <PointTabText isActive={tab === 1}>GPT 개인</PointTabText>
        </PointTabColumn> */}

        {/* <PointTabColumn onClick={() => setTab(2)}>
          <PointTabText isActive={tab === 2}>Grok 개인</PointTabText>
        </PointTabColumn> */}
        <PointTabColumn onClick={() => setTab(2)}>
          <PointTabText isActive={tab === 2}>Perplexity 개인</PointTabText>
        </PointTabColumn>
      </PointTabContainer>

      {tab === 0 && (
        <SubTabContainer>
          <SubTabButton
            type="button"
            isActive={youtubeSubTab === 0}
            onClick={() => handleYoutubeSubTabClick(0)}
          >
            3개월
          </SubTabButton>
          <SubTabButton
            type="button"
            isActive={youtubeSubTab === 1}
            onClick={() => handleYoutubeSubTabClick(1)}
          >
            6개월
          </SubTabButton>
          <SubTabButton
            type="button"
            isActive={youtubeSubTab === 2}
            onClick={() => handleYoutubeSubTabClick(2)}
          >
            12개월
          </SubTabButton>
        </SubTabContainer>
      )}

      {tab === 0 && (
        <YoutubeMatchingInfo
          servsubsidFilter={YOUTUBE_SUBTAB_SERVSUBSID[youtubeSubTab]}
        />
      )}
      {tab === 1 && <GPTSoloMatchingInfo />}
      {tab === 2 && <PerplexitySoloMatchingInfo />}
      {/* {tab === 2 && <GrokSoloMatchingInfo />} */}
    </>
  );
}

const YoutubeTabBar = styled(PointTabBar)`
  width: ${({ tab }) => (tab === 0 ? 80 : tab === 1 ? 80 : 120)}px;
  left: ${({ tab }) => (tab === 0 ? 0 : tab === 1 ? 100 : 100)}px;
`;

const StatusBoard = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const StatusCard = styled.div`
  flex: 1;
  min-width: 200px;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  padding: 20px;
`;

const StatusCardTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #2f2f38;
  margin-bottom: 12px;
`;

const StatusRow = styled.div`
  display: flex;
  gap: 24px;
`;

const StatusItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
`;

const StatusLabel = styled.span`
  font-size: 13px;
  color: #808080;
`;

const StatusValue = styled.span`
  font-size: 22px;
  font-weight: 700;
  color: #1565c0;
`;

const RefreshInfo = styled.div`
  font-size: 12px;
  color: #9e9e9e;
  text-align: right;
  margin-bottom: 12px;
`;

const SubTabContainer = styled.div`
  display: flex;
  gap: 8px;
  margin: 8px 0 16px;
`;

const SubTabButton = styled.button<{ isActive: boolean }>`
  border: 1px solid ${({ isActive }) => (isActive ? "#4a90d9" : "#d9d9d9")};
  background: ${({ isActive }) => (isActive ? "#e9f4ff" : "#ffffff")};
  color: ${({ isActive }) => (isActive ? "#2563eb" : "#666")};
  font-size: 13px;
  font-weight: 600;
  padding: 6px 14px;
  border-radius: 8px;
  cursor: pointer;
`;
